<script lang="ts">
	import WorkflowBeat from '$lib/components/WorkflowBeat.svelte';
	import ExhibitEncounter from '$lib/components/exhibits/ExhibitEncounter.svelte';
	import ExhibitPaQueue from '$lib/components/exhibits/ExhibitPaQueue.svelte';
	import ExhibitDenialPattern from '$lib/components/exhibits/ExhibitDenialPattern.svelte';
	import ExhibitScheduling from '$lib/components/exhibits/ExhibitScheduling.svelte';
	import ExhibitEhrAssistant from '$lib/components/exhibits/ExhibitEhrAssistant.svelte';
	import Signature from '$lib/components/Signature.svelte';
	import Seal from '$lib/components/Seal.svelte';
	import HipaaShield from '$lib/components/HipaaShield.svelte';
	import ApplyModal from '$lib/components/ApplyModal.svelte';
	import ScrollReveal from '$lib/components/ScrollReveal.svelte';
	import { canonicalUrl, jsonLdGraph, ogImageUrl, pageTitle } from '$lib/seo';
	import { trackApplyOpen, trackCtaClick } from '$lib/analytics';

	let { data } = $props();
	const meta = $derived(data.seo!);

	let applyOpen = $state(false);
	function openApply(source: string) {
		trackApplyOpen(source);
		applyOpen = true;
	}

	function onPrimaryCta(source: string) {
		trackCtaClick('Apply to be one of five', source, '/#design-partners');
	}
</script>

<svelte:head>
	<title>{pageTitle(meta)}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={canonicalUrl(meta)} />

	<meta property="og:locale" content="en_US" />
	<meta property="og:site_name" content="Ekavi Care" />
	<meta property="og:type" content="website" />
	<meta property="og:url" content={canonicalUrl(meta)} />
	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:image" content={ogImageUrl(meta)} />
	<meta property="og:image:type" content="image/png" />
	<meta property="og:image:width" content="1200" />
	<meta property="og:image:height" content="630" />
	<meta
		property="og:image:alt"
		content="Ekavi Care — an AI back-office colleague for independent practices"
	/>

	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={meta.title} />
	<meta name="twitter:description" content={meta.description} />
	<meta name="twitter:image" content={ogImageUrl(meta)} />

	{@html `<script type="application/ld+json">${jsonLdGraph(meta)}</script>`}
</svelte:head>

<!-- ============== §00 Hero ==============
	Single-column, role-led. Eyebrow → headline → lede → outcomes →
	CTAs. The three outcome cards carry the proof; CTAs close. The
	morning sentence component still exists in the codebase but no
	longer lives in the hero. -->
<section class="hero" aria-labelledby="hero-headline">
	<div class="container-wide">
		<h1 id="hero-headline" class="hero-headline">
			More care.<br />Less back office.
		</h1>
		<div class="hero-lede-row">
			<p class="hero-lede">
				Ekavi is <strong>the AI back-office colleague</strong> for independent practices. It runs your
				operations across the tools you already use — prior auths, denials, charge capture, scheduling,
				EHR busywork — so providers focus on care, and revenue stops leaking.
			</p>
			<HipaaShield />
		</div>

		<ul class="hero-outcomes" aria-label="Problems we tackle">
			<li class="outcome">
				<span class="outcome-num">13+<span class="outcome-unit">hrs / wk</span></span>
				<span class="outcome-label">spent on prior auth per physician</span>
				<span class="outcome-source">AMA 2024</span>
			</li>
			<li class="outcome">
				<span class="outcome-num">$150K+<span class="outcome-unit">/ year</span></span>
				<span class="outcome-label">lost to undercoding</span>
				<span class="outcome-source">Cosentus 2026 · typical $3M practice</span>
			</li>
			<li class="outcome">
				<span class="outcome-num">36 min<span class="outcome-unit">/ visit</span></span>
				<span class="outcome-label">spent on EHR busywork</span>
				<span class="outcome-source">AMA 2024</span>
			</li>
		</ul>

		<div class="hero-ctas">
			<button
				type="button"
				class="cta-primary"
				onclick={() => {
					onPrimaryCta('hero');
					openApply('hero');
				}}
			>
				Apply to be one of five
				<span class="cta-arrow" aria-hidden="true">→</span>
			</button>
			<a class="cta-secondary" href="#what-it-does">What it does</a>
		</div>
	</div>
</section>

<!-- ============== §01 Five beats — pain → orchestration → outcome ============== -->
<section id="what-it-does" class="section section-beats" aria-labelledby="beats-headline">
	<div class="container-wide">
		<header class="section-intro">
			<p class="eyebrow section-intro-eyebrow">§ 01 · how it works</p>
			<h2 id="beats-headline" class="section-intro-title">What Ekavi does, every day.</h2>
			<p class="section-intro-lede">
				An AI back-office colleague that takes the work off your desk — inside the EHR, at the front
				desk, on prior auth, between the scribe and the chart, and across this week's denials. Each
				one runs on the tools you already use.
			</p>
			<hr class="section-intro-rule" aria-hidden="true" />
		</header>

		<div class="beats">
			<ScrollReveal>
				<WorkflowBeat
					index="01"
					title="EHR busywork, handled."
					body="Thirty-six minutes in the EHR per thirty-minute visit (AMA 2024). Ekavi handles the day-to-day work inside your dense EHR — chart prep, in-basket triage, refill routing, eligibility posting, scheduling moves. Tell it what to do in plain English."
				>
					<ExhibitEhrAssistant />
				</WorkflowBeat>
			</ScrollReveal>

			<ScrollReveal>
				<WorkflowBeat
					index="02"
					title="Scheduling, and the front desk."
					reverse
					body="Forty-two percent of calls go unanswered (Insight 2024). Twenty-three percent of slots no-show (MGMA 2024). Ekavi picks up the phone — books, reschedules, handles insurance questions — and refills the gap from your waitlist before it opens."
				>
					<ExhibitScheduling />
				</WorkflowBeat>
			</ScrollReveal>

			<ScrollReveal>
				<WorkflowBeat
					index="03"
					title="Prior auth, end-to-end."
					body="13h /week /physician on average spent on prior authorizations (AMA 2024). Ekavi runs the queue across your EHR, your clearinghouse, the payer's API, the portal, and a voice agent for residual. No more endless clicks across multiple platforms, Ekavi automates it all without changing your workflows."
				>
					<ExhibitPaQueue />
				</WorkflowBeat>
			</ScrollReveal>

			<ScrollReveal>
				<WorkflowBeat
					index="04"
					title="Coding and claims, in one pass."
					reverse
					body="Practices leak a hundred and fifty thousand a year to undercoding (Cosentus 2026). Ekavi sits between your scribe and your EHR — extracts the ICD-10, CPT, and modifiers for your biller to approve, files the encounter note, posts the chart updates back."
				>
					<ExhibitEncounter />
				</WorkflowBeat>
			</ScrollReveal>

			<ScrollReveal>
				<WorkflowBeat
					index="05"
					title="Denials, recovered."
					body="The same payer-code denial three times in a week. Ekavi clusters them, drafts the appeal with the citations, sends it through your clearinghouse, and tracks recovery to the dollar."
				>
					<ExhibitDenialPattern />
				</WorkflowBeat>
			</ScrollReveal>
		</div>

		<div class="bridge">
			<p class="bridge-text">
				That is the work.<br />
				<em>Five practices help us decide what comes next.</em>
			</p>
			<button type="button" class="cta-primary" onclick={() => openApply('beats')}>
				Apply
				<span class="cta-arrow" aria-hidden="true">→</span>
			</button>
		</div>

		<p class="beats-sources">
			Sources: AMA 2024 Prior Authorization Physician Survey · MGMA Stat (2024, 2025) · Cosentus
			Orthopedic Revenue Report (2026) · Insight Health (2025) · AMA Primary Care EHR Study (2024).
		</p>
	</div>
</section>

<!-- ============== §02 What Ekavi plugs into ============== -->
<section id="stack" class="section section-stack" aria-labelledby="stack-headline">
	<div class="container-wide">
		<header class="section-intro">
			<p class="eyebrow section-intro-eyebrow">§ 02 · what ekavi plugs into</p>
			<h2 id="stack-headline" class="section-intro-title">
				Bring your tools. We make them work as one.
			</h2>
			<p class="section-intro-lede">
				Ekavi does not replace your EHR, your clearinghouse, or the scribe your physicians chose. We
				plug in. Where you don't have something, we bring our own stack or propose a partner — and
				the agentic AI we built handles the actual ops work in between.
			</p>
			<hr class="section-intro-rule" aria-hidden="true" />
		</header>

		<p class="stack-disclaimer">Illustrative</p>
		<dl class="stack-rows">
			<div class="stack-row">
				<dt class="stack-label">EHRs you already use</dt>
				<dd class="stack-marks">
					<span class="stack-logo">
						<img src="/partners/athenahealth.png" alt="athenahealth" loading="lazy" />
					</span>
					<span class="stack-logo">
						<img src="/partners/eclinicalworks.png" alt="eClinicalWorks" loading="lazy" />
					</span>
					<span class="stack-logo">
						<img src="/partners/tebra.png" alt="Tebra" loading="lazy" />
					</span>
				</dd>
			</div>
			<div class="stack-row">
				<dt class="stack-label">Clearinghouses &amp; PA networks</dt>
				<dd class="stack-marks">
					<span class="stack-logo">
						<img src="/partners/availity.png" alt="Availity" loading="lazy" />
					</span>
					<span class="stack-mark">Stedi</span>
					<span class="stack-mark">Change Healthcare</span>
					<span class="stack-logo">
						<img src="/partners/cohere.png" alt="Cohere Health" loading="lazy" />
					</span>
				</dd>
			</div>
			<div class="stack-row">
				<dt class="stack-label">AI scribes</dt>
				<dd class="stack-marks">
					<span class="stack-logo">
						<img src="/partners/abridge.png" alt="Abridge" loading="lazy" />
					</span>
					<span class="stack-logo">
						<img src="/partners/suki.png" alt="Suki" loading="lazy" />
					</span>
					<span class="stack-mark">Ambience</span>
					<span class="stack-mark">DAX (Microsoft)</span>
				</dd>
			</div>
			<div class="stack-row">
				<dt class="stack-label">Voice receptionists</dt>
				<dd class="stack-marks">
					<span class="stack-logo">
						<img src="/partners/hippocratic.png" alt="Hippocratic AI" loading="lazy" />
					</span>
					<span class="stack-mark">Hyro</span>
					<span class="stack-mark">Klara</span>
				</dd>
			</div>
			<div class="stack-row stack-row-note">
				<dt class="stack-label">Existing Payer's APIs</dt>
				<dd class="stack-marks stack-note-text">
					Plus the payer's FHIR Prior Authorization API where it exists (CMS-0057, January 2026
					onward), the payer portal where it doesn't, and the integrated voice agent for the
					residual small-payer cases.
				</dd>
			</div>
		</dl>

		<p class="stack-foot">
			Your EHR or other tool is not on the list? Tell us — we will work to integrate it.
		</p>
	</div>
</section>

<!-- ============== §03 HIPAA 2026 ============== -->
<section class="section section-hipaa" aria-labelledby="hipaa-headline">
	<div class="container-wide">
		<header class="section-intro">
			<p class="eyebrow section-intro-eyebrow">§ 03 · built for what's coming</p>
			<h2 id="hipaa-headline" class="section-intro-title">
				Built with the 2026 HIPAA Security Rule in mind.
			</h2>
			<p class="section-intro-lede">
				The new federal baseline asks for what your auditor was already going to. Ekavi ships ready:
				annual verification on a fixed cadence, an audit log on every action, and a BAA written for
				AI.
			</p>
			<hr class="section-intro-rule" aria-hidden="true" />
		</header>

		<dl class="hipaa-blocks">
			<div class="hipaa-block">
				<dt>Annual vendor verification, on a fixed cadence.</dt>
				<dd>
					A signed BAA is no longer enough. Your annual written verification
					packet — MFA, encryption, segmentation, audit logs, penetration test — already signed,
					dated, and ready for your auditor.
				</dd>
			</div>
			<div class="hipaa-block">
				<dt>Audit log on every action.</dt>
				<dd>
					Seven-year retention. Forensic-grade. Exportable in CSV or signed PDF. The action, the
					model, the confidence, the operator, the outcome — recorded the moment it happens.
				</dd>
			</div>
			<div class="hipaa-block">
				<dt>A BAA written for AI.</dt>
				<dd>
					Ekavi runs locally (not on the cloud) so everything stays local. The models we use offer zero-retention by default.
				</dd>
			</div>
		</dl>

		<p class="hipaa-foot">
			The full architecture is on the trust page. <a href="/trust/">Read the trust page →</a>
		</p>
	</div>
</section>

<!-- ============== §04 First five ============== -->
<section id="design-partners" class="section section-five" aria-labelledby="five-headline">
	<div class="container-wide">
		<header class="section-intro">
			<p class="eyebrow section-intro-eyebrow">§ 04 · design partners</p>
			<h2 id="five-headline" class="section-intro-title">
				Five practices help us decide what comes next.
			</h2>
			<p class="section-intro-lede">
				We are looking for five independent ambulatory practices to help us shape Ekavi.
			</p>
			<hr class="section-intro-rule" aria-hidden="true" />
		</header>

		<div class="five-grid">
			<div class="five-col">
				<p class="five-col-head">What you get</p>
				<ul class="five-list">
					<li>Ninety days, no charge</li>
					<li>Direct line to the founder</li>
					<li>Your roadmap input, in writing</li>
					<li>Quarterly roadmap review you sign off on</li>
					<li>Right to walk away with a 90-day data export</li>
				</ul>
			</div>
			<div class="five-col">
				<p class="five-col-head">What we ask</p>
				<ul class="five-list">
					<li>A real practice (2–49 MDs)</li>
					<li>Twenty minutes a week of practice-manager time</li>
					<li>Permission to learn from anonymized usage</li>
					<li>Honesty about what does not work</li>
				</ul>
			</div>
		</div>

		<div class="five-cta">
			<button type="button" class="cta-primary cta-large" onclick={() => openApply('first-five')}>
				Write me in
				<span class="cta-arrow" aria-hidden="true">→</span>
			</button>
			<p class="five-fine">
				A short form. Reply within a day. Pricing is something the first five help us decide.
			</p>
		</div>
	</div>
</section>

<!-- ============== §05 Close (full-bleed paper) ============== -->
<section class="section section-close" aria-labelledby="close-headline">
	<div class="container">
		<article class="close-letter">
			<p class="close-meta">ekavi.care</p>
			<h2 id="close-headline" class="close-salutation">Dear practice manager,</h2>

			<p class="close-para">
				You want to reduce the administrative burden on your staff (clinical and non-clinical alike),
				without having to change your workflows or operate a costly and long migration, then we think
				the Ekavi we are building is for you.
			</p>

			<p class="close-para">
				Ekavi is your AI back-office assistant that connects to your existing tools and semi-automates
				your day-to-day tasks handling busy EHR work, scheduling and front desk, prior authorizations,
				claims and denials.
				A tool that has the potential to improve revenues and sustainability and lets you focus on care
				and growing your practice.
			</p>

			<p class="close-para">
				We want to build a solution for independent practices with practices, that&#39;s why we need you!
				Five practices, ninety days, no charge. If this sounds like you could benefit from Ekavi, write to us.
			</p>

			<p class="close-sign">
				<Signature size="lg" />
			</p>
			<p class="close-seal">
				<Seal />
			</p>

			<button type="button" class="cta-primary" onclick={() => openApply('close')}>
				Write me in
				<span class="cta-arrow" aria-hidden="true">→</span>
			</button>
		</article>
	</div>
</section>

<ApplyModal bind:open={applyOpen} />

<style>
	/* ============================================================
	   §00 — Hero
	   Single-column, role-led. Eyebrow → headline → lede → three
	   outcome metric cards → CTAs. The metric cards land the proof
	   without competing with the headline (mono numbers + small label).
	   ============================================================ */
	.hero {
		padding-block: var(--ekavi-space-24) var(--ekavi-space-24);
	}
	@media (max-width: 720px) {
		.hero {
			padding-block: var(--ekavi-space-18);
		}
	}
	.hero-headline {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: clamp(2.5rem, 7vw, 4.5rem);
		letter-spacing: -0.025em;
		line-height: 1.02;
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-9);
		max-width: 18ch;
		text-wrap: balance;
	}
	.hero-headline br {
		display: none;
	}
	@media (min-width: 720px) {
		.hero-headline br {
			display: inline;
		}
	}
	.hero-lede {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-20);
		line-height: 1.55;
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-12);
		max-width: 60ch;
		text-wrap: pretty;
	}
	/* Highlight treatment for the role name — filled charcoal pill
	   with white text. Echoes the cta-primary button's color pair so
	   "the AI back-office colleague" reads as the load-bearing claim
	   on the page. `box-decoration-break: clone` keeps the background
	   unbroken across line wraps. */
	.hero-lede strong {
		display: inline;
		background: var(--ekavi-charcoal);
		color: #fff;
		font-weight: var(--ekavi-weight-halbfett);
		letter-spacing: -0.005em;
		padding: 2px 10px;
		border-radius: 0;
		box-decoration-break: clone;
		-webkit-box-decoration-break: clone;
	}

	/* Three outcome cards — the proof line. Mono numbers lead, label
	   below, source attribution at the bottom. Hairline-separated, no
	   filled cards (per brand discipline). */
	.hero-outcomes {
		list-style: none;
		padding: 0;
		margin: 0 0 var(--ekavi-space-12);
		display: grid;
		grid-template-columns: 1fr;
		gap: var(--ekavi-space-6);
		border-top: var(--ekavi-stroke-hairline);
		border-bottom: var(--ekavi-stroke-hairline);
		padding-block: var(--ekavi-space-9);
	}
	@media (min-width: 720px) {
		.hero-outcomes {
			grid-template-columns: repeat(3, 1fr);
			column-gap: var(--ekavi-space-12);
		}
	}
	.outcome {
		display: flex;
		flex-direction: column;
		gap: var(--ekavi-space-2);
	}
	.outcome + .outcome {
		padding-top: var(--ekavi-space-6);
		border-top: var(--ekavi-stroke-hairline);
	}
	@media (min-width: 720px) {
		.outcome + .outcome {
			padding-top: 0;
			padding-left: var(--ekavi-space-12);
			border-top: none;
			border-left: var(--ekavi-stroke-hairline);
			margin-left: calc(-1 * var(--ekavi-space-12));
		}
	}
	.outcome-num {
		font-family: var(--ekavi-font-mono);
		font-size: clamp(2rem, 4.5vw, 2.75rem);
		font-weight: var(--ekavi-weight-kraftig);
		letter-spacing: -0.02em;
		color: var(--ekavi-ink-1);
		font-variant-numeric: tabular-nums lining-nums;
		line-height: 1;
		display: inline-flex;
		align-items: baseline;
		gap: var(--ekavi-space-2);
		flex-wrap: wrap;
	}
	.outcome-unit {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-14);
		font-weight: var(--ekavi-weight-buch);
		color: var(--ekavi-ink-3);
		letter-spacing: 0;
	}
	.outcome-label {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		color: var(--ekavi-ink-1);
		line-height: 1.4;
	}
	.outcome-source {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: 0.02em;
		color: var(--ekavi-ink-3);
		font-variant-numeric: tabular-nums;
		margin-top: var(--ekavi-space-1);
	}

	.hero-ctas {
		display: flex;
		gap: var(--ekavi-space-6);
		flex-wrap: wrap;
		align-items: center;
	}

	/* Hero lede row — lede paragraph on the left, HIPAA shield credential
	   on the right, top-aligned. Wraps to a column on narrow viewports. */
	.hero-lede-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: var(--ekavi-space-9);
		flex-wrap: wrap;
		margin-bottom: var(--ekavi-space-12);
	}
	.hero-lede-row .hero-lede {
		margin: 0;
		flex: 1 1 auto;
	}

	/* ============================================================
	   CTAs (used by every section)
	   ============================================================ */
	.cta-primary {
		appearance: none;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-height: 48px;
		padding: 0 var(--ekavi-space-6);
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		font-weight: var(--ekavi-weight-halbfett);
		letter-spacing: 0.02em;
		color: var(--ekavi-bone);
		background: var(--ekavi-charcoal);
		border: 1px solid var(--ekavi-charcoal);
		cursor: pointer;
		transition:
			opacity var(--ekavi-duration-quick) var(--ekavi-ease-out),
			transform var(--ekavi-duration-base) var(--ekavi-ease-out);
	}
	.cta-primary:hover,
	.cta-primary:focus-visible {
		opacity: 0.94;
		transform: translateY(-2px);
	}
	.cta-primary:active {
		transform: translateY(1px);
	}
	.cta-primary:focus-visible {
		outline: 1px solid var(--ekavi-ochre);
		outline-offset: 3px;
	}
	@media (prefers-reduced-motion: reduce) {
		.cta-primary {
			transition: opacity var(--ekavi-duration-quick) var(--ekavi-ease-out);
		}
		.cta-primary:hover,
		.cta-primary:focus-visible,
		.cta-primary:active {
			transform: none;
		}
	}
	.cta-large {
		min-height: 56px;
		padding: 0 var(--ekavi-space-12);
		font-size: var(--ekavi-text-16);
	}
	.cta-secondary {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		font-weight: var(--ekavi-weight-halbfett);
		color: var(--ekavi-ink-2);
		text-transform: lowercase;
		border-bottom-color: var(--ekavi-hairline);
	}
	.cta-secondary:hover {
		color: var(--ekavi-ink-1);
		border-bottom-color: var(--ekavi-charcoal);
	}

	/* ============================================================
	   §01 — Beats. Wide container so each beat can place its exhibit
	   alongside the text. Top hairline anchors the section visually.
	   ============================================================ */
	.section-beats {
		border-top: var(--ekavi-stroke-hairline);
	}

	/* Centered section intro — used for §01–§04. Eyebrow, display
	   headline, lede paragraph, hairline rule. The display headline uses
	   a manual clamp (px values) because the brand text scale stops at
	   --ekavi-text-40; these introductions earn the extra weight. */
	.section-intro {
		text-align: center;
		max-width: 68ch;
		margin: var(--ekavi-space-6) auto var(--ekavi-space-18);
	}
	.section-intro-eyebrow {
		margin: 0 0 var(--ekavi-space-6);
	}
	.section-intro-title {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: clamp(2.25rem, 5.4vw, 3.5rem); /* 36 → 56px */
		letter-spacing: -0.018em;
		line-height: 1.05;
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-6);
		text-wrap: balance;
	}
	.section-intro-lede {
		font-family: var(--ekavi-font-ui);
		font-size: clamp(1.0625rem, 1.4vw, 1.25rem); /* 17 → 20px */
		line-height: 1.6;
		color: var(--ekavi-ink-2);
		max-width: 56ch;
		margin: 0 auto;
		text-wrap: pretty;
	}
	.section-intro-rule {
		border: none;
		border-top: var(--ekavi-stroke-hairline);
		width: 72px;
		margin: var(--ekavi-space-12) auto 0;
	}
	/* Hairline between consecutive beats (each beat is wrapped in
	   ScrollReveal which renders a `.reveal` div). The cross-component
	   selector reaches in via :global. */
	.beats :global(.reveal + .reveal :is(section)) {
		border-top: var(--ekavi-stroke-hairline);
	}
	.bridge {
		display: flex;
		flex-direction: column;
		gap: var(--ekavi-space-6);
		align-items: flex-start;
		padding-top: var(--ekavi-space-12);
		margin-top: var(--ekavi-space-12);
		border-top: var(--ekavi-stroke-hairline);
	}
	.bridge-text {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-20);
		color: var(--ekavi-ink-1);
		margin: 0;
		max-width: 38ch;
		line-height: 1.4;
		text-wrap: pretty;
	}
	.bridge-text em {
		font-style: italic;
		color: var(--ekavi-ink-2);
	}

	/* Footnote at the bottom of §01 with the source list. Quiet, mono,
	   ink-3. Anchors the per-beat eyebrow citations to a single line. */
	.beats-sources {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: 0.02em;
		color: var(--ekavi-ink-3);
		font-variant-numeric: tabular-nums;
		margin: var(--ekavi-space-9) 0 0;
		max-width: 64ch;
		line-height: 1.55;
	}

	/* ============================================================
	   §02 — Stack ("what Ekavi plugs into"). Row-per-category list.
	   Mono caps label on the left; partner wordmarks flow to the right
	   with hairline dividers between names. Reads as a curated catalog,
	   not a logo soup. (Brand discipline: no graphic logos. The
	   restraint is the point.)
	   ============================================================ */
	.section-stack {
		border-top: var(--ekavi-stroke-hairline);
	}
	.stack-disclaimer {
		font-family: var(--ekavi-font-ui);
		font-style: italic;
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		margin: 0 0 var(--ekavi-space-2);
	}
	.stack-rows {
		margin: 0;
		display: flex;
		flex-direction: column;
		border-top: var(--ekavi-stroke-hairline);
	}
	.stack-row {
		display: grid;
		grid-template-columns: 1fr;
		gap: var(--ekavi-space-3);
		padding-block: var(--ekavi-space-6);
		border-bottom: var(--ekavi-stroke-hairline);
	}
	@media (min-width: 720px) {
		.stack-row {
			grid-template-columns: minmax(0, 1fr) minmax(0, 3fr);
			column-gap: var(--ekavi-space-9);
			align-items: baseline;
		}
	}
	.stack-label {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
		font-weight: var(--ekavi-weight-halbfett);
		margin: 0;
	}
	.stack-marks {
		margin: 0;
		display: flex;
		flex-wrap: wrap;
		gap: var(--ekavi-space-3) var(--ekavi-space-9);
		align-items: center;
	}

	/* Logo wrapper. Holds an <img> at a consistent display height. The
	   muting filter keeps brand colors from competing with bone canvas;
	   the original asset is preserved in /static/partners — only the
	   render is muted. */
	.stack-logo {
		display: inline-flex;
		align-items: center;
		min-height: 28px;
	}
	.stack-logo img {
		display: block;
		height: 28px;
		width: auto;
		max-width: 180px;
		object-fit: contain;
		object-position: left center;
		/* Mute brand colors so the logos sit on the bone canvas rather
		   than competing with it. Hover restores presence without
		   restoring full chroma. */
		filter: grayscale(100%) brightness(0.45) opacity(0.7);
		transition: filter var(--ekavi-duration-quick) var(--ekavi-ease-out);
	}
	.stack-logo:hover img {
		filter: grayscale(100%) brightness(0.25) opacity(0.95);
	}

	/* Typeset wordmark — the brand-discipline fallback for partners
	   whose assets aren't fetchable or whose brand book forbids muting.
	   Sits at the same visual height as the logos. */
	.stack-mark {
		display: inline-flex;
		align-items: center;
		min-height: 28px;
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		font-weight: var(--ekavi-weight-halbfett);
		letter-spacing: -0.005em;
		color: var(--ekavi-ink-1);
		line-height: 1;
	}
	.stack-row-note {
		background: var(--ekavi-bone);
	}
	.stack-note-text {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-13);
		color: var(--ekavi-ink-3);
		line-height: 1.6;
	}
	.stack-foot {
		margin-top: var(--ekavi-space-6);
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		font-style: italic;
		line-height: 1.65;
		color: var(--ekavi-ink-2);
		max-width: 64ch;
	}

	/* ============================================================
	   §03 — HIPAA. Definition list, two-column on wide screens (term
	   left, body right). Mirrors the pattern compliance counsel reads
	   in legal docs — terms anchor the eye.
	   ============================================================ */
	.section-hipaa {
		border-top: var(--ekavi-stroke-hairline);
	}
	.hipaa-blocks {
		margin: 0;
		display: flex;
		flex-direction: column;
	}
	.hipaa-block {
		display: grid;
		grid-template-columns: 1fr;
		gap: var(--ekavi-space-3);
		padding-block: var(--ekavi-space-9);
		border-bottom: var(--ekavi-stroke-hairline);
	}
	.hipaa-block:first-child {
		border-top: var(--ekavi-stroke-hairline);
	}
	@media (min-width: 720px) {
		.hipaa-block {
			grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
			column-gap: var(--ekavi-space-18);
		}
	}
	.hipaa-block dt {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-20);
		letter-spacing: var(--ekavi-track-headers);
		line-height: 1.25;
		color: var(--ekavi-ink-1);
		max-width: 24ch;
		margin: 0;
	}
	.hipaa-block dd {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		line-height: 1.65;
		color: var(--ekavi-ink-2);
		margin: 0;
		max-width: 56ch;
	}
	.hipaa-foot {
		margin-top: var(--ekavi-space-9);
		font-family: var(--ekavi-font-ui);
		font-style: italic;
		color: var(--ekavi-ink-3);
		font-size: var(--ekavi-text-14);
	}

	/* ============================================================
	   §04 — Design partners. Dark island treatment: charcoal bg, bone
	   text, ochre accents. The colour shift is the separator from §03 —
	   hairline rules are not visible at this contrast.
	   ============================================================ */
	.section-five {
		background: var(--ekavi-charcoal);
		color: var(--ekavi-bone);
	}
	.section-five :global(.section-intro-eyebrow) {
		color: var(--ekavi-ochre);
	}
	.section-five :global(.section-intro-title) {
		color: var(--ekavi-bone);
	}
	.section-five :global(.section-intro-lede) {
		color: color-mix(in srgb, var(--ekavi-bone) 80%, transparent);
	}
	.section-five :global(.section-intro-rule) {
		border-top-color: color-mix(in srgb, var(--ekavi-bone) 22%, transparent);
	}

	.five-grid {
		display: grid;
		grid-template-columns: 1fr;
		gap: var(--ekavi-space-4);
		margin-bottom: var(--ekavi-space-12);
	}
	@media (min-width: 720px) {
		.five-grid {
			grid-template-columns: 1fr 1fr;
			gap: var(--ekavi-space-6);
		}
	}
	.five-col {
		background: rgba(245, 241, 234, 0.035);
		border: 1px solid rgba(245, 241, 234, 0.14);
		padding: var(--ekavi-space-6) var(--ekavi-space-6) var(--ekavi-space-4);
	}
	.five-col + .five-col {
		background: transparent;
		border-color: rgba(245, 241, 234, 0.1);
	}
	.five-col-head {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ochre);
		margin: 0 0 var(--ekavi-space-4);
		font-weight: var(--ekavi-weight-halbfett);
	}
	.five-list {
		list-style: none;
		padding: 0;
		margin: 0;
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		color: var(--ekavi-bone);
		line-height: 1.55;
	}
	.five-list li {
		position: relative;
		padding: var(--ekavi-space-3) 0 var(--ekavi-space-3) var(--ekavi-space-4);
		border-bottom: 1px solid rgba(245, 241, 234, 0.1);
	}
	.five-list li:last-child {
		border-bottom: none;
	}
	.five-list li::before {
		content: '';
		position: absolute;
		left: 0;
		top: calc(var(--ekavi-space-3) + 0.78em);
		width: 10px;
		height: 1px;
		background: var(--ekavi-ochre);
	}

	.five-cta {
		display: flex;
		flex-direction: column;
		gap: var(--ekavi-space-4);
		align-items: flex-start;
	}
	.five-cta :global(.cta-primary) {
		background: var(--ekavi-bone);
		color: var(--ekavi-charcoal);
		border-color: var(--ekavi-bone);
	}
	.five-cta :global(.cta-primary:hover) {
		opacity: 0.92;
	}
	.five-cta :global(.cta-primary:focus-visible) {
		outline-color: var(--ekavi-ochre);
	}
	.five-fine {
		margin: 0;
		font-family: var(--ekavi-font-ui);
		font-style: italic;
		font-size: var(--ekavi-text-14);
		color: color-mix(in srgb, var(--ekavi-bone) 60%, transparent);
		max-width: 56ch;
	}

	/* ============================================================
	   §05 — Close. Full-bleed paper-tone background, narrow letter
	   centered inside. The rest of the site is bone; the close tone
	   shifts one step warmer to mark "this is the personal letter."
	   ============================================================ */
	.section-close {
		background: var(--ekavi-paper);
		padding-block: var(--ekavi-space-24);
	}
	@media (max-width: 720px) {
		.section-close {
			padding-block: var(--ekavi-section-y-tight);
		}
	}
	.close-letter {
		max-width: 56ch;
	}
	.close-meta {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
		margin: 0 0 var(--ekavi-space-9);
	}
	.close-salutation {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-28);
		letter-spacing: var(--ekavi-track-headers);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-9);
		font-style: italic;
	}
	.close-para {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		line-height: 1.7;
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-6);
		text-wrap: pretty;
	}
	.close-para em {
		font-style: italic;
		color: var(--ekavi-ink-1);
	}
	.close-sign {
		margin: var(--ekavi-space-12) 0 var(--ekavi-space-3);
	}
	.close-seal {
		margin: 0 0 var(--ekavi-space-12);
	}
</style>
